import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useAppDispatch,
  useAppSelector,
  selectUser,
  selectIsAuthenticated,
  selectPermissions,
} from '@/app/store';
import { signedIn, signedOut, userLoaded } from '@/app/slices/authSlice';
import { authService, type LoginPayload } from '@/lib/api/services/auth.service';
import { toPermissionSet, can as canFn } from './permissions';

/**
 * The app's auth entry point. Wraps the service so components never touch
 * storage or dispatch session actions by hand.
 */
export function useAuth() {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const user = useAppSelector(selectUser);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const permissions = useAppSelector(selectPermissions);

  const login = useCallback(
    async (payload: LoginPayload) => {
      const data = await authService.login(payload);
      dispatch(signedIn(data.user));
      return data;
    },
    [dispatch],
  );

  /**
   * Complete a sign-in obtained OUTSIDE the password flow — the OTP verify
   * response (which carries the user) or an OAuth redirect (tokens only, so the
   * caller fetches the user first). Keeps Redux the single source of identity
   * regardless of how the session was minted.
   */
  const finishSignIn = useCallback(
    (u: Parameters<typeof signedIn>[0]) => {
      dispatch(signedIn(u));
    },
    [dispatch],
  );

  /**
   * Re-pull identity from /auth/me and refresh Redux — for changes made
   * without a full sign-in (e.g. enabling MFA, snoozing its suggestion).
   */
  const refreshUser = useCallback(async () => {
    const u = await authService.me();
    dispatch(userLoaded(u));
    return u;
  }, [dispatch]);

  const logout = useCallback(async () => {
    await authService.logout();
    dispatch(signedOut());
    // Critical: the query cache holds the previous tenant's data. Clearing it
    // prevents the next user from seeing stale records from the last session.
    queryClient.clear();
  }, [dispatch, queryClient]);

  const can = useCallback(
    (required: string) => canFn(toPermissionSet(permissions), required),
    [permissions],
  );

  return { user, isAuthenticated, permissions, login, logout, can, finishSignIn, refreshUser };
}
